import { isArray, isEmpty, isFunction, isString, sortBy } from 'lodash-es';

import {
  ClearFilterService,
  DoFilterService,
  GetFilterFnsService,
  GetFiltersStoreService,
  RegisterFilterService,
  RegisteredFilterFn,
} from 'types';

import { registerService } from './register';

/**
 * When an action is registered setting the order prop to lower or higher will
 * determinate the calling order of registered filters.
 */
const DEFAULT_FILTER_ORDER = 1000;

const initFilterServices = () => {
  const filtersStore: Record<string, RegisteredFilterFn<any, unknown>[]> = {};

  const getFilterFns: typeof GetFilterFnsService = (name) => {
    if (!isString(name) || isEmpty(name)) {
      throw Error('Filter name must be a non-empty string!');
    }

    return filtersStore[name];
  };

  const registerFilter: typeof RegisterFilterService = (name, fn, { order = DEFAULT_FILTER_ORDER } = {}) => {
    if (!isFunction(fn)) {
      throw Error(`registerFilter(${name}): filter function (fn) is missing!`);
    }

    const filterFns = getFilterFns(name) || [];
    filterFns.push({ fn, order });
    filtersStore[name] = sortBy(filterFns, 'order');
  };

  const clearFilter: typeof ClearFilterService = (name, fnOrClearAll) => {
    if (fnOrClearAll === true) {
      // clear all filter functions under the specified name
      delete filtersStore[name];
      return;
    }

    if (!isFunction(fnOrClearAll)) {
      throw Error(`clearFilter(${name}): filter function (fn) is missing!`);
    }

    const filterFns = getFilterFns(name) || [];
    filtersStore[name] = filterFns.filter((filter) => fnOrClearAll !== filter.fn);
    if (isEmpty(filtersStore[name])) {
      delete filtersStore[name];
    }
  };

  const doFilter: typeof DoFilterService = (name, scope, context) => {
    const filterFns = getFilterFns<typeof scope, typeof context>(name);
    if (!isArray(filterFns)) {
      return null;
    }

    return filterFns.reduce((filterValue, { fn }) => fn(filterValue, { name, context }), scope);
  };

  const getFiltersStore: typeof GetFiltersStoreService = () => filtersStore;

  registerService('registerFilter', registerFilter);
  registerService('clearFilter', clearFilter);
  registerService('doFilter', doFilter);
  registerService('getFilterFns', getFilterFns);
  registerService('getFiltersStore', getFiltersStore);
};

export default initFilterServices;
